import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Form, Button, Card, Alert, Spinner, InputGroup } from "react-bootstrap";

const COMPANY_DATA = {
  name: "TORI Viktoriya Manachynska",
  address: "50-202 Wrocław, ul. Księcia Witolda 49/15",
  nip: "NIP: 7822599564",
};

const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

/**
 * Нормалізація грошової суми з поля вводу:
 * - пробіли / нерозривні пробіли як роздільники тисяч;
 * - і крапка, і кома як десятковий роздільник;
 * - формат «1.234,56» / «1,234.56»;
 * - округлення до 2 знаків після коми.
 * Повертає number або null (порожньо) / NaN (некоректно).
 */
const parseMoneyAmount = (raw) => {
  if (raw == null) return null;
  let s = String(raw)
    .trim()
    .replace(/\u00A0/g, " ")
    .replace(/\s+/g, "");
  if (s === "") return null;

  const lastDot = s.lastIndexOf(".");
  const lastComma = s.lastIndexOf(",");
  if (lastDot >= 0 && lastComma >= 0) {
    if (lastComma > lastDot) {
      // 1.234,56
      s = s.replace(/\./g, "").replace(",", ".");
    } else {
      // 1,234.56
      s = s.replace(/,/g, "");
    }
  } else if (lastComma >= 0) {
    s = s.replace(",", ".");
  }

  if (!/^\d+(\.\d+)?$/.test(s)) return NaN;
  const n = Number(s);
  if (!Number.isFinite(n)) return NaN;
  return Math.round(n * 100) / 100;
};

const formatMoneyDisplay = (n) => {
  if (n == null || Number.isNaN(n)) return "";
  return n.toFixed(2);
};

const DriverUploadInvoicePage = () => {
  const { t } = useTranslation();
  const [vatAmount, setVatAmount] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [errors, setErrors] = useState({ vatAmount: "", file: "" });

  const handleVatChange = (e) => {
    // Дозволяємо цифри, крапку, кому, пробіли — решту відкидаємо на льоту
    const cleaned = e.target.value.replace(/[^\d.,\s\u00A0]/g, "");
    setVatAmount(cleaned);
    if (errors.vatAmount) setErrors((prev) => ({ ...prev, vatAmount: "" }));
    setError("");
    setSuccess("");
  };

  const handleVatBlur = () => {
    const parsed = parseMoneyAmount(vatAmount);
    if (parsed != null && !Number.isNaN(parsed) && parsed >= 0) {
      setVatAmount(formatMoneyDisplay(parsed));
    }
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    if (errors.file) setErrors((prev) => ({ ...prev, file: "" }));
    setError("");
    setSuccess("");
  };

  const validate = () => {
    const vat = parseMoneyAmount(vatAmount);
    let vatError = "";
    if (vatAmount.trim() === "" || vat === null) {
      vatError = t("driverInvoiceVatRequired");
    } else if (Number.isNaN(vat) || vat < 0) {
      vatError = t("driverInvoiceVatInvalid");
    }

    let fileError = "";
    if (!file) {
      fileError = t("driverInvoiceFileRequired");
    } else if (!file.type || !file.type.startsWith("image/")) {
      fileError = t("driverInvoiceFileTypeInvalid");
    } else if (file.size > MAX_FILE_SIZE_BYTES) {
      fileError = t("driverInvoiceFileTooLarge", { size: MAX_FILE_SIZE_MB });
    }

    const err = { vatAmount: vatError, file: fileError };
    setErrors(err);
    return !vatError && !fileError;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!validate()) return;

    const vat = parseMoneyAmount(vatAmount);
    if (vat == null || Number.isNaN(vat) || vat < 0) return;

    // Показуємо нормалізоване значення перед відправкою
    setVatAmount(formatMoneyDisplay(vat));

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("vat_amount", formatMoneyDisplay(vat));
      formData.append("image", file);
      const token = localStorage.getItem("token");
      // Не задаємо Content-Type вручну — axios сам поставить multipart/form-data з boundary.
      // Ручний "multipart/form-data" без boundary ламає парсинг на сервері.
      const res = await axios.post("/api/driver-invoices/my/upload", formData, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.data?.success) {
        setSuccess(t("driverInvoiceSuccess"));
        setVatAmount("");
        setFile(null);
        setErrors({ vatAmount: "", file: "" });
        const input = document.getElementById("driver-invoice-file");
        if (input) input.value = "";
      } else {
        setError(res.data?.error || t("driverInvoiceErrorUpload"));
      }
    } catch (err) {
      const status = err.response?.status;
      if (status === 413) {
        setError(err.response?.data?.error || t("driverInvoiceFileTooLarge", { size: MAX_FILE_SIZE_MB }));
      } else {
        setError(err.response?.data?.error || err.message || t("driverInvoiceErrorGeneric"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mt-4">
      <h2 className="mb-4">{t("driverInvoiceUploadTitle")}</h2>

      <Card className="mb-4 border-primary bg-light">
        <Card.Body className="py-3">
          <Card.Title as="h6" className="text-primary mb-2">{t("driverInvoiceUploadDataTitle")}</Card.Title>
          <div className="fw-bold">{COMPANY_DATA.name}</div>
          <div>{COMPANY_DATA.address}</div>
          <div className="fw-bold">{COMPANY_DATA.nip}</div>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          {error && <Alert variant="danger" dismissible onClose={() => setError("")}>{error}</Alert>}
          {success && <Alert variant="success" dismissible onClose={() => setSuccess("")}>{success}</Alert>}

          <Form onSubmit={handleSubmit} noValidate>
            <Form.Group className="mb-3">
              <Form.Label>{t("driverInvoiceVatLabel")} <span className="text-danger">*</span></Form.Label>
              <InputGroup hasValidation>
                <Form.Control
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  placeholder="0.00"
                  value={vatAmount}
                  onChange={handleVatChange}
                  onBlur={handleVatBlur}
                  isInvalid={!!errors.vatAmount}
                />
                <InputGroup.Text>PLN</InputGroup.Text>
                <Form.Control.Feedback type="invalid">{errors.vatAmount}</Form.Control.Feedback>
              </InputGroup>
              <Form.Text className="text-muted d-block mt-1">
                {t("driverInvoiceVatHint")}
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t("driverInvoiceFileLabel")} <span className="text-danger">*</span></Form.Label>
              <Form.Control
                id="driver-invoice-file"
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                isInvalid={!!errors.file}
              />
              {errors.file && <Form.Text className="text-danger d-block mt-1">{errors.file}</Form.Text>}
              <Form.Text className="text-muted d-block">
                {t("driverInvoiceFileHint")}
              </Form.Text>
              {file && (
                <div className="small mt-1">
                  {t("driverInvoiceFileSelected")} {file.name}
                  {file.size ? ` (${(file.size / 1024 / 1024).toFixed(2)} MB)` : ""}
                </div>
              )}
            </Form.Group>

            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? <Spinner animation="border" size="sm" className="me-2" /> : null}
              {t("driverInvoiceSubmitBtn")}
            </Button>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default DriverUploadInvoicePage;
